/**
 * OnboardingWizard Component
 *
 * Multi-step wizard for initial app setup.
 * Converted from Figma design to Polaris components.
 *
 * This is the main orchestrator component that:
 * - Manages wizard state (current step, selected products, direction)
 * - Renders step components from app/components/onboarding/
 * - Handles navigation between steps
 * - Provides callbacks for completion and skipping
 *
 * Step Components (extracted to app/components/onboarding/):
 * - StepIndicator: Progress bar with step circles
 * - ProductSelectorStep: Step 1 - Select products to monitor
 * - ThresholdStep: Step 2 - Configure alert thresholds
 * - NotificationsStep: Step 3 - Configure notification preferences
 * - SuccessStep: Step 4 - Success confirmation screen
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Icon,
  Box,
  Tooltip,
} from '@shopify/polaris';
import {
  ChevronLeftIcon,
  ProductIcon,
  SettingsIcon,
  NotificationIcon,
  CheckIcon,
} from '@shopify/polaris-icons';
import { StockSentryLogo } from '../ui/StockSentryLogo';
import { RetailForgeLogo } from '../ui/RetailForgeLogo';
import { AppBridgeInlineModal } from '../app-bridge/AppBridgeModal';

// Import extracted components from onboarding directory
import { StepIndicator } from './StepIndicator';
import { ProductSelectorStep } from './ProductSelectorStep';
import { ThresholdStep } from './ThresholdStep';
import { NotificationsStep } from './NotificationsStep';
import { SuccessStep } from './SuccessStep';
// Import types from types file
import type { StepId, WizardProduct, Step } from './types';

// Import shared constants
import { BRAND_PURPLE } from '../../constants/brand';

// Re-export types for external consumers
export type { WizardProduct } from './types';

/**
 * Props for the main OnboardingWizard component
 */
export interface OnboardingWizardProps {
  /** Available products to monitor */
  products: WizardProduct[];
  /** Callback when wizard completes with selected product IDs */
  onComplete: (selectedProducts: string[]) => void;
  /** Optional callback to skip onboarding */
  onSkip?: () => void;
}

/**
 * Step configuration for the wizard
 */
const steps: Step[] = [
  {
    id: 'select-products',
    title: 'Select products',
    icon: ProductIcon,
    showInProgress: true,
  },
  {
    id: 'set-thresholds',
    title: 'Set thresholds',
    icon: SettingsIcon,
    showInProgress: true,
  },
  {
    id: 'notifications',
    title: 'Notifications',
    icon: NotificationIcon,
    showInProgress: true,
  },
  {
    id: 'success',
    title: 'Complete',
    icon: CheckIcon,
    showInProgress: false,
  },
];

/**
 * Main OnboardingWizard Component
 *
 * Orchestrates the multi-step onboarding flow with:
 * - Product selection
 * - Threshold configuration
 * - Notification preferences
 * - Success confirmation
 */
export function OnboardingWizard({ products, onComplete, onSkip }: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState<StepId>('select-products');
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [logoSpin, setLogoSpin] = useState(false);
  // Track if entrance animations have completed (prevent re-animation on step change)
  const [hasEntered, setHasEntered] = useState(false);
  // Per-product threshold state
  const [thresholds, setThresholds] = useState({ urgentDays: 3, warningDays: 7 });
  // Verified email address (null if not verified)
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  // Modal state - controlled open/close for AppBridgeModal
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const [emailWarningModalOpen, setEmailWarningModalOpen] = useState(false);

  // Mark entrance animations as complete after initial render
  useEffect(() => {
    const timer = setTimeout(() => setHasEntered(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const currentStepIndex = steps.findIndex((s) => s.id === currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStep === 'notifications'; // Last step before success
  const isSuccessStep = currentStep === 'success';
  const canContinue = currentStep === 'select-products' ? selectedProducts.size > 0 : true;

  const handleProductToggle = useCallback((productId: string) => {
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }, []);

  const handleBack = () => {
    if (!isFirstStep) {
      // No animation on back - only animate on forward navigation
      setDirection('backward');
      setCurrentStep(steps[currentStepIndex - 1].id);
    }
  };

  const handleContinue = () => {
    if (isLastStep) {
      // Check if email is verified before finishing
      if (!isEmailVerified) {
        setEmailWarningModalOpen(true);
        return;
      }
      // Navigate to success screen
      setLogoSpin(true);
      setTimeout(() => setLogoSpin(false), 400);
      setDirection('forward');
      setCurrentStep('success');
    } else if (!isSuccessStep) {
      setLogoSpin(true);
      setTimeout(() => setLogoSpin(false), 400);
      setDirection('forward');
      setCurrentStep(steps[currentStepIndex + 1].id);
    }
  };

  const handleFinishWithoutEmail = () => {
    setEmailWarningModalOpen(false);
    setDirection('forward');
    setCurrentStep('success');
  };

  const handleGoToDashboard = () => {
    onComplete(Array.from(selectedProducts));
  };

  const handleConfigureAnother = () => {
    // Return to product selection without clearing selections
    setDirection('backward');
    setCurrentStep('select-products');
  };

  return (
    <>
      {/* Shiny overlay background with brand purple gradient */}
      <div className="wizard-shiny-background" aria-hidden="true" />

      <Box paddingBlockStart="500" paddingInline="400" position="relative" zIndex="1">
        <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
          <BlockStack gap="500">
            {/* Header - Unified branding matching dashboard */}
            <BlockStack gap="300" inlineAlign="center">
              <InlineStack gap="200" blockAlign="center">
                <div className={`wizard-header-logo-icon${!hasEntered ? ' wizard-stagger-item wizard-entrance-logo' : ''}${logoSpin ? ' wizard-logo-animate' : ''}`}>
                  <StockSentryLogo size={28} color="white" animate={logoSpin} />
                </div>
                <div className={!hasEntered ? 'wizard-stagger-item wizard-entrance-title' : ''}>
                  <BlockStack>
                    <Text as="h1" variant="headingXl" fontWeight="bold">
                      StockSentry
                    </Text>
                    <a
                      href="https://retailforge.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="wizard-header-subtext"
                    >
                      By RetailForge <RetailForgeLogo size={14} />
                    </a>
                  </BlockStack>
                </div>
              </InlineStack>
              <div className={!hasEntered ? 'wizard-stagger-item wizard-entrance-tagline' : ''}>
                <Text as="p" variant="bodyLg" alignment="center">
                  Let's set up your inventory alerts in{' '}
                  <Tooltip
                    content="You can change these settings anytime after the initial setup"
                    preferredPosition="above"
                  >
                    <Text as="span" variant="bodyLg" fontWeight="bold">
                      <span
                        style={{ color: BRAND_PURPLE, cursor: 'help', borderBottom: '1px dashed currentColor' }}
                      >
                        3 simple steps
                      </span>
                    </Text>
                  </Tooltip>
                </Text>
              </div>
            </BlockStack>

            {/* Step Indicator - Hide on success screen */}
            {!isSuccessStep && <StepIndicator currentStep={currentStep} steps={steps} />}

            {/* Main Content Card */}
            <div className={!hasEntered ? 'wizard-stagger-item wizard-entrance-card' : ''}>
              <Card>
                <Box padding="500">
                  {currentStep === 'select-products' && (
                    <div
                      key="step-select-products"
                      className={direction === 'forward' ? 'step-slide-forward' : 'step-slide-backward'}
                    >
                      <ProductSelectorStep
                        products={products}
                        selectedProducts={selectedProducts}
                        onSelectionChange={handleProductToggle}
                      />
                    </div>
                  )}
                  {currentStep === 'set-thresholds' && (
                    <div
                      key="step-set-thresholds"
                      className={direction === 'forward' ? 'step-slide-forward' : 'step-slide-backward'}
                    >
                      <ThresholdStep
                        selectedProducts={products.filter(p => selectedProducts.has(p.id))}
                        onViewProducts={() => {
                          setDirection('backward');
                          setCurrentStep('select-products');
                        }}
                        onThresholdsChange={(newThresholds) => {
                          setThresholds({
                            urgentDays: newThresholds.urgentThreshold,
                            warningDays: newThresholds.warningThreshold,
                          });
                        }}
                      />
                    </div>
                  )}
                  {currentStep === 'notifications' && (
                    <div
                      key="step-notifications"
                      className={direction === 'forward' ? 'step-slide-forward' : 'step-slide-backward'}
                    >
                      <NotificationsStep
                        onSettingsChange={(settings) => {
                          setIsEmailVerified(settings.isVerified);
                          if (settings.isVerified && settings.emailAddress) {
                            setVerifiedEmail(settings.emailAddress);
                          } else {
                            setVerifiedEmail(null);
                          }
                        }}
                      />
                    </div>
                  )}
                  {currentStep === 'success' && (
                    <div
                      key="step-success"
                      className={direction === 'forward' ? 'step-slide-forward' : 'step-slide-backward'}
                    >
                      <SuccessStep
                        products={products.filter(p => selectedProducts.has(p.id))}
                        thresholds={thresholds}
                        verifiedEmail={verifiedEmail}
                        onGoToDashboard={handleGoToDashboard}
                        onConfigureAnother={handleConfigureAnother}
                      />
                    </div>
                  )}
                </Box>
              </Card>
            </div>

            {/* Navigation Buttons - Hide on success screen */}
            {!isSuccessStep && (
              <div className={!hasEntered ? 'wizard-stagger-item wizard-entrance-nav' : ''}>
                <InlineStack align="space-between" blockAlign="center">
                  {/* Help Link on first step, Back button otherwise */}
                  {isFirstStep ? (
                    <Text as="span" variant="bodyMd" tone="subdued">
                      Need help?{' '}
                      <a
                        href="/app/help"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: BRAND_PURPLE,
                          textDecoration: 'underline',
                          fontSize: '13px',
                          fontFamily: 'inherit',
                        }}
                      >
                        Visit our help center
                      </a>
                    </Text>
                  ) : (
                    <Button
                      icon={ChevronLeftIcon}
                      onClick={handleBack}
                      accessibilityLabel="Back"
                    >
                      Back
                    </Button>
                  )}

                  <InlineStack gap="200">
                    {onSkip && (
                      <Button onClick={() => setSkipModalOpen(true)} accessibilityLabel="Skip onboarding">
                        Skip for now
                      </Button>
                    )}
                    <Button variant="primary" onClick={handleContinue} disabled={!canContinue}>
                      {isLastStep ? 'Finish' : 'Continue'}
                    </Button>
                  </InlineStack>
                </InlineStack>
              </div>
            )}
          </BlockStack>
        </div>
      </Box>

      {/* Skip Confirmation Modal - AppBridgeModal for proper admin overlay */}
      {onSkip && (
        <AppBridgeInlineModal
          id="skip-onboarding-modal"
          open={skipModalOpen}
          onClose={() => setSkipModalOpen(false)}
          title="Skip setup wizard?"
          variant="base"
          primaryAction={{
            content: 'Skip setup',
            onAction: () => {
              setSkipModalOpen(false);
              setDirection('forward');
              setCurrentStep('success');
            },
          }}
          secondaryActions={[
            {
              content: 'Continue setup',
              onAction: () => setSkipModalOpen(false),
            },
          ]}
        >
          <BlockStack gap="400">
            <Text as="p" variant="bodyMd">
              Are you sure you want to skip the first-time setup wizard? You can still configure your alerts manually from the dashboard.
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              <Tooltip content="Access setup wizard anytime from the Help menu">
                <span style={{ borderBottom: '1px dashed currentColor', cursor: 'help' }}>
                  You can always return to this wizard
                </span>
              </Tooltip>
              {' '}via the Help Center.
            </Text>
          </BlockStack>
        </AppBridgeInlineModal>
      )}

      {/* Email Warning Modal - shown when finishing without email verification */}
      <AppBridgeInlineModal
        id="email-warning-modal"
        open={emailWarningModalOpen}
        onClose={() => setEmailWarningModalOpen(false)}
        title="No email configured"
        variant="base"
        primaryAction={{
          content: 'Continue without email',
          onAction: handleFinishWithoutEmail,
        }}
        secondaryActions={[
          {
            content: 'Add email',
            onAction: () => setEmailWarningModalOpen(false),
          },
        ]}
      >
        <BlockStack gap="300">
          <Text as="p" variant="bodyMd">
            You haven't verified an email address. Without email notifications, you won't receive stock alerts.
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            You can add and verify an email address anytime in Settings → Notifications.
          </Text>
        </BlockStack>
      </AppBridgeInlineModal>
    </>
  );
}
